import { useCallback, useMemo } from 'react';
import queryString, { ParsedQuery } from 'query-string';

import { useLogger } from '@/shared/hooks/useLogger';
import { useLocale } from '@/shared/hooks/useLocale';
import { clientConfig } from '@/config/client';

import { getRelativeUrlWithoutLocale } from '../lib/locale';

const {
    i18n: { displayLanguages },
} = clientConfig;

interface Params {
    url?: string;
    query?: ParsedQuery;
    locale?: string;
    isFallbackLocale?: boolean;
}

function getLocaleUrlPart({ locale, isFallbackLocale }: Params) {
    return locale && !isFallbackLocale ? `/${locale}` : '';
}

function getUrlWithLocale({ url = '', query = {}, locale, isFallbackLocale }: Params = {}) {
    const [path, search] = url.split('?');

    return queryString.stringifyUrl({
        url:
            `${getLocaleUrlPart({ locale, isFallbackLocale })}${
                path === '/' ? `${search ? `?${search}` : ''}` : url
            }` || '/',
        query,
    });
}

export const useDisplayLanguage = () => {
    const logger = useLogger();
    const locale = useLocale();

    const displayLanguage = useMemo(() => {
        const selectedLanguageCode = locale.lang;

        return displayLanguages?.find((language) => language.code === selectedLanguageCode);
    }, [locale.lang]);

    const setDisplayLanguage = useCallback(
        (language: string) => {
            logger.debug(`change language to ${language}`);

            const { pathname, search } = window.location;

            window.location.assign(
                getUrlWithLocale({
                    url: getRelativeUrlWithoutLocale(`${pathname}${search}`, locale.lang),
                    locale: language,
                })
            );
        },
        [logger, locale]
    );

    return [displayLanguage, setDisplayLanguage] as const;
};
